import { HStack } from '@chakra-ui/react';
import { MimeTypeEnum } from '@monetize/types/common';
import { addMonthsCustom } from '@monetize/utils/core';
import { endOfMonth } from 'date-fns/endOfMonth';
import { format as formatDate } from 'date-fns/format';
import { parse as parseDate } from 'date-fns/parse';
import { startOfDay } from 'date-fns/startOfDay';
import { startOfMonth } from 'date-fns/startOfMonth';
import { useEffect, useState } from 'react';
import { useExportRevRec, useRevRecSchedule } from '../../api/revenueService';
import {
  MBox,
  MButton,
  MCurrencySelect,
  MFlex,
  MFormField,
  MPageContainer,
  MPageHeader,
  MPageLoader,
} from '../../components/Monetize';
import { MCustomSelect } from '../../components/Monetize/MCustomSelect/MCustomSelect';
import MEmptyDataPlaceholder from '../../components/Monetize/MEmptyDataPlaceholder';
import { MReadOnlyInput } from '../../components/Monetize/MReadOnlyInput';
import { useCurrencies } from '../../hooks/useCurrencies';
import { useDocumentHead } from '../../services/documentHead';
import { getMonthlyDropdownItems } from '../../utils';
import { saveFile } from '../../utils/download';
import { RevRecChart } from './RevRecChart';
import { RevRecTable } from './RevRecTable';

const DEFAULT_CURRENCY = 'USD';
const DEFAULT_FROM = formatDate(
  addMonthsCustom(startOfMonth(new Date()), -1),
  'yyyy-MM',
);
const DEFAULT_TO = formatDate(
  addMonthsCustom(endOfMonth(new Date()), 11),
  'yyyy-MM',
);
const DEFAULT_RECOGNIZED_FROM = formatDate(
  addMonthsCustom(endOfMonth(new Date()), 11),
  'yyyy-MM',
);

export const RevRecPage = () => {
  const { setDocTitle } = useDocumentHead();
  useEffect(() => {
    setDocTitle('Revenue');
  }, []);

  const {
    currencies,
    defaultCurrency,
    isLoading: isLoadingCurrencies,
  } = useCurrencies();

  const [startPeriod, setStartPeriod] = useState(DEFAULT_FROM);
  const [endPeriod, setEndPeriod] = useState(DEFAULT_TO);
  const [endDateFormatted, setEndDateFormatted] = useState(DEFAULT_TO);
  const [recognizedAsOfPeriod, setRecognizedAsOfPeriod] = useState(
    DEFAULT_RECOGNIZED_FROM,
  );
  const [currency, setCurrency] = useState<string>(DEFAULT_CURRENCY);
  const [startDateOptions] = useState(() =>
    getMonthlyDropdownItems(new Date(2021, 0, 1), new Date()),
  );
  const [endDateOptions, setEndDateOptions] = useState<
    { label: string; value: string }[]
  >([]);

  useEffect(() => {
    if (defaultCurrency) {
      setCurrency(defaultCurrency);
    }
  }, [defaultCurrency]);

  useEffect(() => {
    setEndDateFormatted(
      formatDate(parseDate(endPeriod, 'yyyy-MM', new Date()), 'MMM yyyy'),
    );
  }, [endPeriod]);

  useEffect(() => {
    const newEndDate = addMonthsCustom(
      parseDate(startPeriod, 'yyyy-MM', startOfMonth(new Date())),
      11,
    );
    setEndPeriod(formatDate(newEndDate, 'yyyy-MM'));
    setRecognizedAsOfPeriod(formatDate(newEndDate, 'yyyy-MM'));
    setEndDateOptions(getMonthlyDropdownItems(newEndDate, 6));
  }, [startPeriod]);

  const { data: revRecData, isLoading } = useRevRecSchedule(
    { startPeriod, endPeriod, recognizedAsOfPeriod, currency },
    {
      enabled:
        !!startPeriod && !!endPeriod && !!recognizedAsOfPeriod && !!currency,
      meta: { showErrorToast: true },
    },
  );

  const { mutate: doDownload } = useExportRevRec({
    // eslint-disable-next-line @typescript-eslint/no-shadow
    onSuccess: (csv, { startPeriod, endPeriod }) => {
      saveFile(
        csv,
        `rev-rec-waterfall_${startPeriod}_to_${endPeriod}.csv`,
        MimeTypeEnum.CSV,
      );
    },
    onError: () => {},
  });

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const handleStartDateChange = (startDate: string) => {
    const newStartDate = parseDate(
      startDate,
      'yyyy-MM',
      startOfDay(new Date()),
    );
    setStartPeriod(formatDate(newStartDate, 'yyyy-MM'));
  };
  const handleCurrencyChange = (value: string) => {
    setCurrency(value);
  };
  const isEmpty = !isLoading && revRecData?.recognitionSchedule.length === 0;

  return (
    <MPageContainer enableAccountSearch maxWidth="100%" data-testid="revenue">
      <MPageHeader title="Revenue Recognition" />
      <MBox w="100%" h="100%">
        {(isLoading || isLoadingCurrencies) && <MPageLoader />}

        {!isLoading && !isLoadingCurrencies && revRecData && (
          <>
            <MFlex justifyContent="space-between" alignItems="end" mb={2}>
              <HStack gap={4}>
                <MFormField label="Start Date">
                  <MCustomSelect
                    showQueryInput
                    items={startDateOptions}
                    itemTitle="label"
                    itemValue="value"
                    value={startPeriod}
                    onChange={(e) => handleStartDateChange(`${e}`)}
                  />
                </MFormField>
                <MFormField label="Until" maxW={20}>
                  <MReadOnlyInput value={endDateFormatted} />
                </MFormField>
                {currencies.length > 1 && (
                  <MFormField label="Currency" maxW={24}>
                    <MCurrencySelect
                      value={currency}
                      onChange={(e) => handleCurrencyChange(`${e}`)}
                    />
                  </MFormField>
                )}
                <MFormField label="Recognized from">
                  <MCustomSelect
                    showQueryInput
                    items={endDateOptions}
                    itemTitle="label"
                    itemValue="value"
                    value={recognizedAsOfPeriod}
                    onChange={(e) => setRecognizedAsOfPeriod(`${e}`)}
                  />
                </MFormField>
              </HStack>
              {!isEmpty && (
                <MFlex>
                  <MButton
                    variant="secondary"
                    onClick={() =>
                      doDownload({
                        startPeriod,
                        endPeriod,
                        recognizedAsOfPeriod,
                        currency: revRecData.currency || DEFAULT_CURRENCY,
                      })
                    }
                  >
                    Download CSV
                  </MButton>
                </MFlex>
              )}
            </MFlex>
            {isEmpty ? (
              <>
                <MEmptyDataPlaceholder
                  mainMessage="No revenue data"
                  smallMessage="Choose a different period range."
                />
              </>
            ) : (
              <>
                <RevRecTable revRecData={revRecData} />
                <RevRecChart revRecData={revRecData} mt={12} pb={24} />
              </>
            )}
          </>
        )}
      </MBox>
    </MPageContainer>
  );
};
